import React, { useEffect } from 'react';
import styles from './usersManagement.module.scss';
import Arrow from '../../../assets/icons/caret.svg';
import {
  useAsyncDebounce,
  useBlockLayout,
  useExpanded,
  useFilters,
  useGlobalFilter,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import Search from '../../../assets/icons/search.svg';
import Button from '../../../components/Button';
import routes from '../../../routing/config';
import Trash from '../../../assets/icons/trash.svg';
import Order from '../../../assets/icons/order.svg';
import Edit from '../../../assets/icons/edit.svg';
import ActiveCheckbox from '../../../assets/img/active-check.svg';
import InactiveCheckbox from '../../../assets/img/inactive-checkbox.svg';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getUsersList } from '../../../store/actions/users';
import GlobalState from '../../../store/reducers/globalState.interface';
import { normalizeUsers } from '../../../utils/normalizeUsers';
import api from '../../../api';
import ModalDialog from '../../../components/ModalDialog';
import Clear from '../../../assets/icons/clear.svg';

const UsersManagement: React.FC = (): JSX.Element => {
  const history = useNavigate();
  const updatedUsers = useSelector((state: GlobalState) => state.users.users);

  const deleteUser = async (id: number) => {
    await api.users.deleteUser(id);
    dispatch(getUsersList());
  };
  const dispatch = useDispatch();
  const data = React.useMemo(
    () => normalizeUsers(updatedUsers),
    [updatedUsers],
  );
  const columns = React.useMemo(
    () => [
      {
        Header: 'User Name',
        accessor: 'fullName',
        width: 300,
      },
      {
        Header: 'Metamask 1',
        accessor: 'metamaskWallet1',
        width: 520,
      },
      {
        Header: 'Metamask 2',
        accessor: 'metamaskWallet2',
        width: 520,
      },
      {
        Header: 'Permission Mode',
        accessor: 'permissionMode',
      },
      {
        Header: 'Active',
        accessor: 'active',
        width: 100,
      },
      {
        Header: '',
        accessor: 'delete',
        width: 40,
      },
      {
        Header: '',
        accessor: 'edit',
        width: 40,
      },
    ],
    [],
  );
  useEffect(() => {
    dispatch(getUsersList());
  }, []);

  const tableInstance = useTable(
    {
      //@ts-ignore
      columns,
      data,
      initialState: {
        //@ts-ignore
        sortBy: [
          {
            id: 'tokenName',
            desc: false,
          },
        ],
      },
    },
    useBlockLayout,
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    useRowSelect,
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    preGlobalFilteredRows,
    setGlobalFilter,
    state,
  }: any = tableInstance;

  const GlobalFilter = ({ globalFilter, setGlobalFilter }: any) => {
    const [value, setValue] = React.useState(globalFilter);
    const search = useAsyncDebounce(() => {
      setGlobalFilter(value || undefined);
    }, 200);

    const handleKeyDown = (e: any) => {
      if (e.key === 'Enter') {
        search();
      }
    };

    return (
      <div className={styles.searchContainer}>
        <input
          className={styles.searchInput}
          value={value || ''}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={`Search`}
        />
        <div onClick={() => setGlobalFilter('')} className={styles.clearButton}>
          <img className={styles.searchIcon} src={Clear} alt="clear" />
        </div>
        <div onClick={search} className={styles.searchButton}>
          <img className={styles.searchIcon} src={Search} alt="search" />
        </div>
      </div>
    );
  };

  const CustomCell = ({ cell }: any) => {
    if (typeof cell.value === 'boolean') {
      return (
        <td {...cell.getCellProps()}>
          <div style={{ marginLeft: 10 }}>
            <img
              src={cell.value ? ActiveCheckbox : InactiveCheckbox}
              alt="inactive"
            />
          </div>
        </td>
      );
    }

    if (
      cell.column.id === 'delete' ||
      cell.column.id === 'market' ||
      cell.column.id === 'edit'
    ) {
      return (
        <td {...cell.getCellProps()}>
          {cell.column.Header}
          {cell.column.id === 'delete' && (
            <ModalDialog
              onSubmit={() => {
                deleteUser(cell.value);
              }}
            >
              <img
                style={{ cursor: 'pointer' }}
                src={Trash}
                onClick={() => {}}
              />
            </ModalDialog>
          )}
          {cell.column.id === 'market' && <img src={Order} />}
          {cell.column.id === 'edit' && (
            <img
              style={{ cursor: 'pointer' }}
              onClick={() => {
                history(routes.addUser.route, {
                  state: {
                    user: cell?.row?.original,
                  },
                });
              }}
              src={Edit}
            />
          )}
        </td>
      );
    }
    return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
  };

  return (
    <div>
      <div className={styles.headerBlock}>
        <div className={styles.title}>Users Management</div>
        <div className={styles.dFlex}>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              history(routes.addUser.route);
            }}
          >
            + ADD
          </Button>
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </div>
      </div>
      <div className={styles.borderSpacingDiv}>
        <table className={styles.borderSpacing} {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup: any) => (
              <tr
                className={styles.tableHeader}
                {...headerGroup.getHeaderGroupProps()}
              >
                {headerGroup.headers.map((column: any) => (
                  <th
                    className={styles.tableColumnWrapper}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <img
                            src={Arrow}
                            className={styles.arrowBottom}
                            alt="expanded"
                          />
                        ) : (
                          <img
                            src={Arrow}
                            className={styles.arrowTop}
                            alt="expanded"
                          />
                        )
                      ) : (
                        <span className={styles.emptyArrow} />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={styles.tableRows} {...getTableBodyProps()}>
            {rows.map((row: any, i: any) => {
              prepareRow(row);
              return (
                <tr className={styles.tableRow} {...row.getRowProps()}>
                  {row.cells.map((cell: any) => {
                    return <CustomCell cell={cell} />;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default UsersManagement;
